import React__default, { forwardRef, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { AkChevronDownIcon } from '@fuegokit/fuegoicons-react';
import RuleItemInnerVisualSlot from './_RuleItemInnerVisualSlot.js';
import RuleItemUserActions from './RuleItemUserActions.js';
import sx from '../../sx.js';
import { themeGet } from '../../utils/theme-get.js';
import Button from '../Button/Button.js';
import Box from '../Box/Box.js';
import { useBoolean } from '../../hooks/useBoolean.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden.js';
import Collapse from '../Collapse/Collapse.js';
import Text from '../Text/Text.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// Toggle Button
// ------------------------------------------------
const StyledToggleButton = styled(Button).withConfig({
  displayName: "CollapsibleRuleItem__StyledToggleButton",
  componentId: "sc-sp8q4a-0"
})(["appearance:none;margin:0;padding:0;border:none;background:transparent;cursor:pointer;text-align:left;font:inherit;user-select:none;display:flex;align-items:center;color:inherit;font-weight:normal;font-size:", ";text-decoration:none;opacity:0.85;font-weight:", ";padding:", " ", ";border-radius:", ";&:hover,&:focus{opacity:1;text-decoration:none;color:", ";background-color:", ";cursor:", ";}&:active{opacity:1;text-decoration:none;background-color:", ";color:", ";}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}svg{transition:transform 0.15s ease;}&[data-state='open']{--icon-rotation:180deg;background-color:", ";color:", ";svg{transform:rotate(var(--icon-rotation));}}", ""], themeGet('fontSizes.2'), themeGet('fontWeights.semibold'), themeGet('space.1'), themeGet('space.1'), themeGet('radii.1'), p => p.isRuleItemDisabled ? themeGet('colors.text.disabled') : themeGet('colors.link.default'), p => p.isRuleItemDisabled ? themeGet('colors.background.neutral.subtle.default') : themeGet('colors.background.neutral.subtle.hovered'), p => p.isRuleItemDisabled ? 'not-allowed' : '', themeGet('colors.background.selected.default.[default]'), themeGet('colors.text.selected'), themeGet('colors.border.focused'), themeGet('colors.border.focused'), themeGet('colors.background.selected.default.[default]'), themeGet('colors.text.selected'), sx);
const ToggleButton = ({
  children,
  onOpenChange,
  isRuleItemDisabled,
  open,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(StyledToggleButton, _extends({
    "data-state": open ? 'open' : '',
    isRuleItemDisabled: isRuleItemDisabled
  }, props), children);
};
ToggleButton.displayName = "ToggleButton";
const spacingMap = {
  none: '',
  default: '6px',
  compact: themeGet('space.0'),
  relaxed: themeGet('space.3')
};
const borderStylesMap = {
  default: '1px solid',
  selected: '1px solid',
  hovered: '1px solid' // Add this line for the hover case
};

const borderColorMap = {
  default: p =>
  // eslint-disable-next-line no-nested-ternary
  p.isSelected ? themeGet('colors.border.selected') : p.disabled ? 'transparent' : themeGet('colors.border.default'),
  hover: p =>
  // eslint-disable-next-line no-nested-ternary
  p.isSelected ? themeGet('colors.border.selected') : p.disabled === true ? 'transparent' : themeGet('colors.border.default')
};
const Wrapper = styled(Box).withConfig({
  displayName: "CollapsibleRuleItem__Wrapper",
  componentId: "sc-sp8q4a-1"
})(["border-radius:", ";display:flex;flex-direction:column;align-items:flex-start;justify-content:space-between;justify-items:center;gap:", ";border:1px solid;border-color:", ";transition:background-color 0.1s ease-out,box-shadow 0.15s cubic-bezier(0.47,0.03,0.49,1.38);transition:", ";border-color:", ";background-color:", ";color:", ";user-select:none;padding-top:", ";padding-bottom:", ";padding-left:", ";padding-right:", ";&:hover{background-color:", ";border:", ";border-color:", ";cursor:", ";}", " &:focus{border-color:", ";}&:active{background-color:", ";border:1px solid ", ";}", " .rule-item-visual{svg,rect{fill:", ";}}", ""], themeGet('radii.2'), themeGet('space.4'), themeGet('colors.border.default'), p => p.isSelected ? 'revert' : '350ms', p => p.isSelected ? themeGet('colors.border.selected') : 'transparent', p => p.disabled ? themeGet('colors.background.disabled') : themeGet('colors.background.neutral.default.[default]'), p => p.disabled ? themeGet('colors.text.disabled') : themeGet('colors.text.default'), p => spacingMap[p.spacing], p => spacingMap[p.spacing], p => p.spacing === 'none' ? '' : themeGet('space.3'), p => p.spacing === 'none' ? '' : themeGet('space.3'), p => p.disabled ? themeGet('colors.background.disabled') : themeGet('colors.background.neutral.subtle.default'), borderStylesMap.hovered, p => borderColorMap.hover(p), p => p.disabled ? 'not-allowed' : 'pointer', p => p.isSelected && css(["&:hover{box-shadow:0 0 0 2px ", ";transition:background-color 0.1s ease-out,box-shadow 0.45s cubic-bezier(0.47,0.03,0.49,1.38);}"], themeGet('colors.border.focused')), themeGet('colors.border.focused'), themeGet('colors.background.neutral.subtle.default'), themeGet('colors.border.default'), p => p.disabled && css(["background-color:", ";color:", ";svg:{fill:", ";}"], themeGet('colors.background.disabled'), themeGet('colors.text.disabled'), themeGet('colors.icon.disabled')), p => p.disabled ? themeGet('colors.icon.disabled') : '', sx);

// Rule item right title and description
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RuleItemRight = ({
  title = 'What',
  description = 'Description of what'
}) => {
  return /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    as: "p",
    sx: {
      fontWeight: 'semibold'
    }
  }, title), /*#__PURE__*/React__default.createElement(Text, {
    as: "p"
  }, description));
};
RuleItemRight.displayName = "RuleItemRight";
/**
 * Rule item with collapsible content passed in as children. Provide a title and description.
 */
const CollapsibleRuleItem = /*#__PURE__*/forwardRef(({
  title,
  description,
  children,
  onOpenChange,
  ...props
}, forwardedRef) => {
  const {
    loading,
    counter,
    spacing = 'default',
    loaderPosition = 'auto',
    leadingVisual: LeadingVisual,
    trailingVisual: TrailingVisual,
    trailingAction,
    isSelected: isSelectedProp,
    disabled: disabledProp,
    sx: sxProp,
    onSelect,
    as = 'div'
  } = props;
  const [open, {
    toggle
  }] = useBoolean();
  const containerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, containerRef);
  const showLeadingLoadingIndicator = loading && (loaderPosition === 'leading' || Boolean(LeadingVisual && loaderPosition !== 'trailing'));
  const showTrailingLoadingIndicator = loading && (loaderPosition === 'trailing' || Boolean(loaderPosition === 'auto' && !LeadingVisual));
  const [isActive, setIsActive] = useState(isSelectedProp);
  const isRuleItemSelected = isSelectedProp || isActive;
  const [isDisabled, setIsDisabled] = useState(disabledProp);
  const isRuleItemDisabled = disabledProp || isDisabled;
  const handleRuleItemSelect = useCallback(event => {
    if (isSelectedProp) return;
    if (onSelect) {
      // onSelect(event, event.clientY)
      onSelect(event);
    }
  }, [isSelectedProp, onSelect]);
  function handleRuleItemBlur() {
    setIsActive(false);
  }
  useOutsideClick({
    onClickOutside: handleRuleItemBlur,
    containerRef
  });
  return /*#__PURE__*/React__default.createElement(Wrapper, {
    as: as,
    ref: containerRef,
    isSelected: isRuleItemSelected,
    onClick: handleRuleItemSelect,
    disabled: isRuleItemDisabled,
    sx: sxProp,
    spacing: spacing
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      gap: 2,
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(RuleItemInnerVisualSlot, {
    visualPosition: "leading",
    showLoadingIndicator: showLeadingLoadingIndicator,
    hasLoadingIndicator: typeof loading === 'boolean'
  }, typeof LeadingVisual === 'function' ? /*#__PURE__*/React__default.createElement(LeadingVisual, null) : LeadingVisual), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(RuleItemRight, {
    title: title,
    description: description
  }), /*#__PURE__*/React__default.createElement(RuleItemInnerVisualSlot, {
    visualPosition: "trailing",
    showLoadingIndicator: showTrailingLoadingIndicator,
    hasLoadingIndicator: typeof loading === 'boolean'
  }, typeof TrailingVisual === 'function' ? /*#__PURE__*/React__default.createElement(TrailingVisual, null) : TrailingVisual, /*#__PURE__*/React__default.createElement(ToggleButton, {
    disabled: isRuleItemDisabled,
    isRuleItemDisabled: isRuleItemDisabled,
    onClick: toggle,
    open: open
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, null, "Open"), /*#__PURE__*/React__default.createElement(AkChevronDownIcon, null))))), /*#__PURE__*/React__default.createElement(Collapse, {
    in: open
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      color: 'text.default',
      pb: 4,
      paddingLeft: 2
    }
  }, children), /*#__PURE__*/React__default.createElement(RuleItemUserActions, null)));
});
CollapsibleRuleItem.displayName = 'CollapsibleRuleItem';
var CollapsibleRuleItem$1 = CollapsibleRuleItem;

export { ToggleButton, CollapsibleRuleItem$1 as default };
